import { ReactNode, useContext } from "react";
import { Button, Divider, Grid, Link, Typography } from "@mui/material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Card, CardHeader, CardContent } from "@mui/material";

import {
  ContentCopy as CopyIcon,
  Input as InputIcon,
  OpenInNew,
} from "@mui/icons-material";

import { ApplicantDoc, ApplicantDocument } from "../../types/applicants";
import { VisaApplication } from "../../types/visa-application";
import { applicantFullName } from "../../utils/applicant-utils";
import { useNavigate } from "react-router-dom";
import { Sponsor } from "../../types/sponsors";
import { Agent } from "../../types/agents";
import { VirtualPresentation } from "../../types/virtual-presentations";
import { getGenderLabel } from "../../utils/inputs/persons-inputs";
import {
  getApplicantDocumentAccessUrl,
  getPreferredApplicantDocument,
} from "../../data-functions/applicant-documents-api";
import { AuthContext } from "../auth-provider";
import { ApplicantTermsAcceptanceSummary } from "../terms/applicant-terms-acceptance-summary";

interface ApplicationDetailsProps {
  application: VisaApplication;
  applicant: ApplicantDoc;
  sponsor?: Sponsor;
  agent?: Agent;
  presentation?: VirtualPresentation;
  isGeneratingEmailExtensionCode?: boolean;
  onGenerateEmailExtensionCode?: (applicantId: string) => void | Promise<void>;
  onCopyEmailExtensionCode?: (code: string) => void;
}

export const ApplicationDetails = ({
  application,
  applicant,
  sponsor,
  agent,
  presentation,
  isGeneratingEmailExtensionCode = false,
  onGenerateEmailExtensionCode,
  onCopyEmailExtensionCode,
}: ApplicationDetailsProps) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext)!;

  const person = applicant.applicant;
  const passportImageDocument = getPreferredApplicantDocument(
    person,
    "passport_image",
  );
  const ukWorkVisaDocument = getPreferredApplicantDocument(
    person,
    "uk_work_visa_image",
  );

  const handleOpenDocument = async (document?: ApplicantDocument) => {
    if (!document) {
      return;
    }

    if (document.url) {
      window.open(document.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (!(document.storagePath && currentUser?.authUser)) {
      return;
    }

    try {
      const url = await getApplicantDocumentAccessUrl(applicant.id, document, {
        authToken: await currentUser.authUser.getIdToken(),
      });

      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Failed to open applicant document", error);
      window.alert("The document could not be opened. Please try again.");
    }
  };

  const renderDocumentLink = (
    label: ReactNode,
    document?: ApplicantDocument,
  ) => {
    return (
      <Grid item xs={12} md={4} lg={3}>
        <Typography variant="button" sx={{ fontWeight: "bold" }}>
          {label}:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {document && (document.url || document.storagePath) ? (
            <>
              <Link
                component="button"
                onClick={() => {
                  void handleOpenDocument(document);
                }}
              >
                Open image
              </Link>
              <OpenInNew fontSize="small" color="action" />
            </>
          ) : (
            <Typography>n/a</Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              {applicantFullName(person)} ({person.email})
              <Tooltip title="Go to Applicant">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigate(`/applicants/${applicant.id}`);
                  }}
                  sx={{ color: "white", marginLeft: 1 }}
                >
                  <InputIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, marginRight: 1 }}
              >
                Email code:
              </Typography>
              {person.email_extension_code ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {person.email_extension_code}
                  </Typography>
                  <Tooltip title="Copy email code">
                    <IconButton
                      size="small"
                      onClick={() =>
                        person.email_extension_code &&
                        onCopyEmailExtensionCode?.(person.email_extension_code)
                      }
                      sx={{ color: "white" }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onGenerateEmailExtensionCode?.(applicant.id)}
                  disabled={isGeneratingEmailExtensionCode}
                  sx={{ color: "white", borderColor: "white" }}
                >
                  Generate code
                </Button>
              )}
            </Box>
          </Box>
        }
      ></CardHeader>
      <CardContent>
        <Grid container spacing={2} padding={2}>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Sponsor:
            </Typography>
            <Typography>{sponsor ? sponsor.name : "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={8} lg={4}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Agent:
            </Typography>
            <Typography>{agent ? agent.company : "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Visa Type:
            </Typography>
            <Typography>{application.type.label}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Alt. starting date:
            </Typography>
            <Typography>
              {application.alternate_start_date
                ? application.alternate_start_date
                : "n/a"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              CoS number:
            </Typography>
            <Typography>{application.cos ? application.cos : "n/a"}</Typography>
          </Grid>
          {/* 2nd line -------------------------- */}
          <Grid item xs={6} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Survey expiry date:
            </Typography>
            <Typography>
              {application.dataForm_expiry_date
                ? application.dataForm_expiry_date
                : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={4}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Survey completed:
            </Typography>
            <Typography>
              {application.dataForm_completed
                ? application.dataForm_completed
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Privacy policy accepted:
            </Typography>
            <Typography>
              {application.privacy_policy_accepted ? "yes" : "no"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Sufficient funds:
            </Typography>
            <Typography>
              {application.sufficient_funds ? "yes" : "no"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              GWF number:
            </Typography>
            <Typography>{application.gwf ? application.gwf : "n/a"}</Typography>
          </Grid>
          {/* 3rd line -------------------------- */}

          <Grid item xs={12}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Visa application link:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography>
                {application.visa_application_link
                  ? application.visa_application_link
                  : "n/a"}
              </Typography>
              {application.visa_application_link && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    window.open(application.visa_application_link, "_blank")
                  }
                  sx={{ ml: 1 }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
          {/* 4rd line -------------------------- */}
          <Grid item xs={12} lg={3}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Virtual Presentation:
            </Typography>
            <Typography>
              {presentation ? presentation.name : "Not Assigned"}
            </Typography>
          </Grid>
          {application.virtualPresentation?.virtualPresentationId && (
            <>
              <Grid item xs={6} lg={2}>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  Presentation expiry date:
                </Typography>
                <Typography>
                  {application.virtualPresentation.expiry_date
                    ? application.virtualPresentation.expiry_date
                    : "n/a"}
                </Typography>
              </Grid>
              <Grid item xs={6} lg={3}>
                <Typography variant="button" sx={{ fontWeight: "bold" }}>
                  Presentation completed:
                </Typography>
                <Typography>
                  {application.virtualPresentation.completedDate
                    ? application.virtualPresentation.completedDate
                    : "-"}
                </Typography>
              </Grid>
            </>
          )}
          {!application.virtualPresentation?.virtualPresentationId && (
            <Grid item xs={6} lg={9}></Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Name:
            </Typography>
            <Typography>{person.name || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Surname:
            </Typography>
            <Typography>{person.surname || "n/a"}</Typography>
          </Grid>

          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Nationality:
            </Typography>
            <Typography>{person.nationality?.label || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Date of birth:
            </Typography>
            <Typography>{person.birth_date || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Place of birth:
            </Typography>
            <Typography>{person.birth_place || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Country of birth:
            </Typography>
            <Typography>{person.birth_country?.label || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Gender:
            </Typography>
            <Typography>{getGenderLabel(person.gender)}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Passport number:
            </Typography>
            <Typography>
              {person.passport ? person.passport.id : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Passport issue date:
            </Typography>
            <Typography>
              {person.passport ? person.passport.issue_date : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Passport expiry date:
            </Typography>
            <Typography>
              {person.passport ? person.passport.expiry_date : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Place of issue:
            </Typography>
            <Typography>
              {person.passport ? person.passport.issuing_place : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Country of issue:
            </Typography>
            <Typography>
              {person.passport?.issuing_country
                ? person.passport.issuing_country?.label
                : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} lg={4}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Permanent address:
            </Typography>
            <Typography>{person.residency_street || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              City:
            </Typography>
            <Typography>{person.residency_city || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Country:
            </Typography>
            <Typography>
              {person.residency_country
                ? person.residency_country?.label
                : "n/a"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Region:
            </Typography>
            <Typography>{person.residency_province || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              Postcode:
            </Typography>
            <Typography>{person.residency_zip || "n/a"}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {renderDocumentLink("Passport image", passportImageDocument)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderDocumentLink("UK work visa image", ukWorkVisaDocument)}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <ApplicantTermsAcceptanceSummary
            applicantId={applicant.id}
            title="Accepted terms"
          />
        </Grid>
      </CardContent>
    </Card>
  );
};
